import React from 'react'
import { Check, Send, Store } from 'lucide-react'
import styles from './styles.module.css'

interface IPublishingLoaderProps {
  title: string
  subtitle?: string
  complete?: boolean
  paused?: boolean
  /** Actual backend progress only. Omit when the request has no progress events. */
  progress?: { sent: number; total: number; label: string }
}

const PALETTE = [
  ['#d5e8f0', '#3f8bb0'],
  ['#fde2dc', '#d9573a'],
  ['#fbe1ec', '#d6387f'],
  ['#f6e3f3', '#b0479b'],
  ['#e3efd2', '#5b7627'],
  ['#e8efff', '#3b6fd6'],
]
const NODES = Array.from({ length: 8 }, (_, index) => {
  const angle = (index / 8) * Math.PI * 2 - Math.PI / 2 + Math.PI / 8
  const x = Math.cos(angle)
  const y = Math.sin(angle)
  return { x, y, tint: PALETTE[index % 6][0], ink: PALETTE[index % 6][1], delay: `${index * 0.42}s` }
})

/** Decorative eight-node broadcast. Node checks never represent individual recipients. */
export function PublishingLoader({
  title,
  subtitle,
  complete = false,
  paused = false,
  progress,
}: IPublishingLoaderProps) {
  const measured = progress && progress.total > 0
  const percent = complete
    ? 100
    : measured
    ? Math.min(100, Math.max(0, (progress!.sent / progress!.total) * 100))
    : undefined

  return (
    <section className={styles.panel} data-complete={complete} data-paused={paused} aria-busy={!complete && !paused}>
      <div className={styles.content}>
        <svg className={styles.illustration} viewBox="0 0 380 380" aria-hidden="true" focusable="false" direction="ltr">
          <circle cx="190" cy="190" r="86" fill="#f7f8f9" />
          <circle className={styles.orbit} cx="190" cy="190" r="168" />
          <circle className={styles.ring} cx="190" cy="190" r="86" />
          <circle className={`${styles.ring} ${styles.secondRing}`} cx="190" cy="190" r="86" />
          {NODES.map(({ x, y, tint, ink, delay }, index) => (
            <g key={index} style={{ '--delay': delay, '--tint': tint, '--ink': ink } as React.CSSProperties}>
              <line
                className={styles.guide}
                x1={190 + x * 54}
                y1={190 + y * 54}
                x2={190 + x * 117}
                y2={190 + y * 117}
              />
              <line className={styles.draw} x1={190 + x * 54} y1={190 + y * 54} x2={190 + x * 117} y2={190 + y * 117} />
              <g transform={`translate(${190 + x * 54} ${190 + y * 54})`}>
                <circle
                  className={styles.fly}
                  r="4"
                  style={{ '--dx': `${x * 63}px`, '--dy': `${y * 63}px` } as React.CSSProperties}
                />
              </g>
              <g transform={`translate(${190 + x * 146} ${190 + y * 146})`}>
                <circle className={styles.arrive} r="23" />
                <circle r="23" fill="white" stroke="#eceef1" />
                <Store x="-7" y="-7" width="14" height="14" color="#c2c6cc" />
                <g className={styles.lit}>
                  <circle r="23" fill={tint} />
                  <Store x="-7" y="-7" width="14" height="14" color={ink} />
                </g>
                <g transform="translate(18 18)">
                  <g className={styles.check}>
                    <circle r="9" fill="#5b7627" stroke="white" strokeWidth="2.5" />
                    <Check x="-5" y="-5" width="10" height="10" color="white" strokeWidth="3" />
                  </g>
                </g>
              </g>
            </g>
          ))}
          <circle className={styles.core} cx="190" cy="190" r="48" fill="#1d1f22" />
          <g transform="translate(172 172)">
            <g className={styles.plane}>
              <Send width="36" height="36" color="white" strokeWidth="1.7" />
            </g>
          </g>
        </svg>
        <div className={styles.copy} role="status" aria-live="polite">
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className={styles.progress}>
          <div
            className={styles.track}
            role="progressbar"
            aria-label={title}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div
              className={`${styles.fill} ${percent == null ? styles.estimated : ''}`}
              style={percent == null ? undefined : { width: `${percent}%` }}
            />
          </div>
          {measured && <p>{progress!.label}</p>}
        </div>
      </div>
    </section>
  )
}
